// src/pages/Dashboard/Dashboard.js

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Layout & Components
import Header from "../../components/Layout/Header";
import Menu from "../../components/Layout/Menu";
import ItemList from "../../components/Item/ItemList";
import Modal from "../../components/Layout/Modal";
import ItemForm from "../../components/Item/ItemForm";
import AssignStockForm from "../../components/Item/AssignStockForm";
import StockHistory from "../../components/Item/StockHistory";
import CityUpdates from "../../components/Item/CityUpdates";

// RTK Query Hooks
import {
  useGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
} from "../../redux/slices/itemApiSlice";
import {
  useGetUsersQuery,
  useGetUserAssignedItemsQuery,
  useAssignItemToUsersBulkMutation,
  useUpdateUserAssignedItemMutation,
  useDeleteUserAssignedItemMutation,
} from "../../redux/slices/usersApiSlice";

// Icons, CSS
import styles from "./Dashboard.module.css";
import reportStyles from "../../components/Item/StockReports.module.css";
import { IoSearchOutline, IoAddOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import {
  typeLabels,
  sortItemsByGroup,
  sortByName,
  matchesSearch,
  normalizeName,
} from "../../utils/items";

const errorMessage = (err, fallback) => err?.data?.message || fallback;

const findAssignment = (user, itemId) =>
  (user.assignedItems || []).find(
    (ai) => ai.item?._id === itemId || ai.item === itemId
  );

const Dashboard = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const isAdmin = userInfo?.isSuperAdmin;

  // Seçimler & Modal Kontrolleri
  const [selectedUser, setSelectedUser] = useState(null);

  const [isItemFormOpen, setItemFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const [isCityUpdatesOpen, setCityUpdatesOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null); // { userId, itemId, name }

  // “Detay” modal
  const [distributionItem, setDistributionItem] = useState(null);

  // Arama / filtre
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("all");

  // Şehir değişince arama ve filtre sıfırlansın
  useEffect(() => {
    setSearchTerm("");
    setView("all");
  }, [selectedUser]);

  // RTK Query: Items, Users
  const {
    data: items,
    refetch: refetchItems,
    isLoading: isLoadingItems,
  } = useGetItemsQuery(null, {
    skip: !isAdmin,
  });

  const activeUserId = isAdmin && selectedUser ? selectedUser : userInfo?._id;

  // Kullanıcının atanmış stokları
  const {
    data: userAssignedItems,
    isLoading: isLoadingUserAssignedItems,
  } = useGetUserAssignedItemsQuery(activeUserId, {
    // Admin genel görünümde kendi atamalarına ihtiyaç duymaz
    skip: !userInfo || (isAdmin && !selectedUser),
    // Şehre her tıklandığında güncel veriyi getir
    refetchOnMountOrArgChange: true,
  });

  // Tüm kullanıcılar (admin)
  const { data: users, refetch: refetchUsers } = useGetUsersQuery(null, {
    skip: !isAdmin,
    refetchOnMountOrArgChange: true,
  });

  // Mutations
  const [createItem] = useCreateItemMutation();
  const [updateItem] = useUpdateItemMutation();
  const [assignItemToUsersBulk] = useAssignItemToUsersBulkMutation();
  const [deleteItem] = useDeleteItemMutation();
  const [updateUserAssignedItem] = useUpdateUserAssignedItemMutation();
  const [deleteUserAssignedItem] = useDeleteUserAssignedItemMutation();

  // Login kontrol
  useEffect(() => {
    if (!userInfo) {
      navigate("/login");
    }
  }, [userInfo, navigate]);

  const cityUsers = (users || []).filter((u) => !u.isSuperAdmin);

  // ÜRÜN EKLE / DÜZENLE
  const handleOpenItemForm = (item = null) => {
    setEditingItem(item);
    setItemFormOpen(true);
  };

  const handleCloseItemForm = () => {
    setItemFormOpen(false);
    setEditingItem(null);
  };

  const handleSaveItem = async (data) => {
    const payload = {
      name: data.name,
      type: data.type,
      issueNumber: data.type === "book" ? null : data.issueNumber,
    };
    try {
      if (editingItem) {
        await updateItem({ _id: editingItem._id, ...payload }).unwrap();
        toast.success("Ürün güncellendi");
        refetchUsers();
      } else {
        await createItem(payload).unwrap();
        toast.success("Ürün eklendi");
      }
      handleCloseItemForm();
      refetchItems();
    } catch (err) {
      toast.error(
        errorMessage(err, editingItem ? "Ürün güncellenemedi" : "Ürün eklenemedi")
      );
    }
  };

  // ŞEHRE ATA (toplu)
  const handleAssignStock = async (itemId, userIds) => {
    try {
      const res = await assignItemToUsersBulk({ itemId, userIds }).unwrap();
      setAssignModalOpen(false);
      toast.success(`Ürün ${res.assignedCount} şehre atandı`);
    } catch (error) {
      toast.error(errorMessage(error, "Ürün atanamadı"));
    }
  };

  // ÜRÜN SİL
  const handleDeleteItem = async (itemId) => {
    try {
      await deleteItem(itemId).unwrap();
      refetchItems();
      refetchUsers();
      toast.success("Ürün silindi");
    } catch (error) {
      toast.error(errorMessage(error, "Ürün silinemedi"));
    }
  };

  // Silmeden önce ürünün kaç şehirde, kaç adet stoğu olduğunu göster
  const itemDeleteMessage = (item) => {
    const assignments = cityUsers
      .map((u) => findAssignment(u, item._id))
      .filter(Boolean);
    const total = assignments.reduce((sum, ai) => sum + (ai.count || 0), 0);
    const base = `"${item.name}" ürününü silmek istediğinizden emin misiniz?`;
    if (assignments.length === 0) return `${base}\n\nBu ürün hiçbir şehre atanmamış.`;
    return (
      `${base}\n\nBu ürün ${assignments.length} şehre atanmış, toplam ${total} adet stok kaydı var. ` +
      `Silerseniz tüm şehirlerden kaldırılır ve geri alınamaz.`
    );
  };

  const assignedItemDeleteMessage = (item) =>
    `"${item.name}" bu şehrin listesinden kaldırılacak (mevcut stok: ${
      item.count ?? 0
    }). Emin misiniz?`;

  // ATANMIŞ STOK MİKTARI GÜNCELLE (satır içi)
  const handleCountSave = async (item, count) => {
    try {
      await updateUserAssignedItem({
        userId: activeUserId,
        assignedItemId: item.assignedItemId,
        data: { count },
      }).unwrap();
      toast.success(`${item.name}: ${count}`, { autoClose: 1500 });
    } catch (error) {
      toast.error(errorMessage(error, "Stok güncellenemedi"));
    }
  };

  // ATANMIŞ STOK SİL
  const handleDeleteAssignedItem = async (assignedItemId) => {
    try {
      await deleteUserAssignedItem({
        userId: activeUserId,
        assignedItemId,
      }).unwrap();
    } catch (error) {
      toast.error(errorMessage(error, "Atanmış stok silinemedi"));
    }
  };

  // STOK GEÇMİŞİ
  const handleOpenHistory = (item) => {
    setHistoryTarget({ userId: activeUserId, itemId: item._id, name: item.name });
  };

  // DETAY (Distribution): her render'da güncel kullanıcı verisinden hesaplanır
  const distributionData = distributionItem
    ? sortByName(cityUsers).map((u) => {
        const found = findAssignment(u, distributionItem._id);
        return {
          userId: u._id,
          userName: u.name,
          assigned: Boolean(found),
          count: found ? found.count || 0 : 0,
        };
      })
    : [];
  const distributionAssigned = distributionData.filter((d) => d.assigned);
  const distributionTotal = distributionAssigned.reduce((s, d) => s + d.count, 0);

  // Her item için atanmış toplam stok hesapla
  const getItemAssignedTotal = (itemId) =>
    cityUsers.reduce((total, user) => {
      const assigned = findAssignment(user, itemId);
      return total + (assigned ? assigned.count || 0 : 0);
    }, 0);

  // CSV Export
  const handleExportCSV = async () => {
    // Şehirlerin son girdiği stokları almak için listeyi tazele
    let latestUsers = users;
    try {
      const result = await refetchUsers();
      latestUsers = result?.data || users;
    } catch (error) {
      // tazeleme başarısızsa eldeki veriyle devam
    }
    if (!latestUsers) return;

    // Excel'in Türkçe ayarı ";" ayırıcı bekler; alanlar tırnakla korunur
    const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

    const headers = ["Şehir", "Email", "Ürün İsmi", "Kategori", "Sayı No", "Stok Sayısı"];
    const csvLines = [headers.map(escapeCell).join(";")];

    sortByName(latestUsers)
      .filter((u) => !u.isSuperAdmin)
      .forEach((u) => {
        sortItemsByGroup(
          (u.assignedItems || [])
            .filter((ai) => ai.item)
            .map((ai) => ({ ...ai.item, count: ai.count }))
        ).forEach((it) => {
          csvLines.push(
            [
              u.name,
              u.email,
              normalizeName(it.name),
              typeLabels[it.type] || "Bilinmiyor",
              it.type === "book" ? "" : it.issueNumber ?? "",
              it.count || 0,
            ]
              .map(escapeCell)
              .join(";")
          );
        });
      });

    // BOM: Excel'in Türkçe karakterleri doğru göstermesi için
    const csvContent = "﻿" + csvLines.join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "allUsersStocks.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Filtre / Arama (tüm görünümlerde)
  const applyFilters = (list) =>
    list
      .filter((it) => (view === "all" ? true : it.type === view))
      .filter((it) => matchesSearch(it.name, searchTerm));

  const safeItems = items || []; // fallback boş dizi
  const filteredItems = applyFilters(sortItemsByGroup(safeItems));

  // Silinmiş ürüne ait (item=null) atamalar listelenmez
  const safeUserAssignedItems = (userAssignedItems || []).filter((ai) => ai.item);
  const filteredUserAssignedItems = applyFilters(
    sortItemsByGroup(
      safeUserAssignedItems.map((ai) => ({
        ...ai.item,
        count: ai.count,
        assignedItemId: ai._id,
        stockUpdatedAt: ai.stockUpdatedAt || null,
      }))
    )
  );

  const isFiltering = searchTerm.trim() !== "" || view !== "all";
  const emptyText = isFiltering ? "Aramaya uygun ürün bulunamadı" : "Stok bulunamadı";

  // İstatistikler hesapla
  const totalStockCount = cityUsers.reduce(
    (sum, user) =>
      sum +
      (user.assignedItems || []).reduce(
        (acc, ai) => acc + (ai.item ? ai.count || 0 : 0),
        0
      ),
    0
  );
  const totalBooks = safeItems.filter(it => it.type === 'book').length;
  const totalMagazines = safeItems.filter(it => it.type === 'magazine').length;
  const totalChildrenMagazines = safeItems.filter(it => it.type === 'childrenMagazine').length;
  // Admin: en az bir şehre atanmış farklı ürün sayısı; şehir: kendi toplam stoğu
  const assignedItemsCount = isAdmin
    ? new Set(
        cityUsers.flatMap((u) =>
          (u.assignedItems || [])
            .filter((ai) => ai.item)
            .map((ai) => ai.item._id || ai.item)
        )
      ).size
    : safeUserAssignedItems.reduce((sum, ai) => sum + (ai.count || 0), 0);

  const selectedUserName = users?.find((u) => u._id === selectedUser)?.name || "";

  return (
    <div className={styles.container}>
      <Header />
      <div style={{ display: "flex", height: "92%" }}>
        <Menu
          selectedUser={selectedUser}
          setView={setView}
          setSelectedUser={setSelectedUser}
        />
        <div className={styles.content}>
          {/* İstatistik Kartları */}
          {isAdmin && !selectedUser && (
            <div className={styles.statsBox}>
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Toplam Stok</div>
                <div className={styles.statValue}>{totalStockCount}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Kitap</div>
                <div className={styles.statValue}>{totalBooks}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Dergi</div>
                <div className={styles.statValue}>{totalMagazines}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Çocuk Dergisi</div>
                <div className={styles.statValue}>{totalChildrenMagazines}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Atanmış Ürün</div>
                <div className={styles.statValue}>{assignedItemsCount}</div>
              </div>
            </div>
          )}

          {!isAdmin && (
            <div className={styles.statsBox}>
              <div className={styles.statCard}>
                <div className={styles.statLabel}>Benim Toplam Stoğum</div>
                <div className={styles.statValue}>{assignedItemsCount}</div>
              </div>
            </div>
          )}

          <div className={styles.mainBox}>
            <div className={styles.mainBoxHeader}>
              <div className={styles.mainTop}>
                <span className={styles.categoryText}>
                  {isAdmin
                    ? selectedUser
                      ? `Şehir Stokları - ${selectedUserName}`
                      : "Dashboard - Tüm Stoklar"
                    : "Atanmış Stoklarım"}
                </span>
                {isAdmin && !selectedUser && (
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      className={styles.addButton}
                      onClick={() => handleOpenItemForm()}
                    >
                      <IoAddOutline size={20} /> Ürün Ekle
                    </button>

                    <button
                      className={styles.addButton}
                      onClick={() => setAssignModalOpen(true)}
                    >
                      <IoAddOutline size={20} /> Şehre Ata
                    </button>

                    <button
                      className={styles.addButton}
                      onClick={() => setCityUpdatesOpen(true)}
                    >
                      Şehir Takibi
                    </button>

                    <button className={styles.csvbtn} onClick={handleExportCSV}>
                      CSV Export
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.searchAndSort}>
                <div className={styles.search}>
                  <IoSearchOutline size={20} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
                <div className={styles.sort}>
                  {[
                    ["all", "Tümü"],
                    ["book", "Kitap"],
                    ["magazine", "Dergi"],
                    ["childrenMagazine", "Çocuk Dergisi"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      className={styles.addButton}
                      style={view === value ? { outline: "2px solid #007bff" } : undefined}
                      onClick={() => setView(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {!isAdmin && (
                <p className={reportStyles.muted}>
                  Miktarı kutuya yazıp Enter'a basarak veya ✓ butonuyla kaydedebilirsiniz.
                </p>
              )}
            </div>
          </div>

          {/* Listeleme */}
          <div className={styles.listBox}>
            {isAdmin && !selectedUser ? (
              isLoadingItems ? (
                <p>Yükleniyor...</p>
              ) : (
                <ItemList
                  items={filteredItems.map((it) => ({
                    ...it,
                    count: getItemAssignedTotal(it._id),
                    stockUpdatedAt: null,
                  }))}
                  onDelete={handleDeleteItem}
                  deleteConfirmMessage={itemDeleteMessage}
                  onEdit={handleOpenItemForm}
                  isSuperAdmin={isAdmin}
                  onDistributionClick={setDistributionItem}
                  emptyText={emptyText}
                />
              )
            ) : isLoadingUserAssignedItems ? (
              <p>Yükleniyor...</p>
            ) : (
              <ItemList
                items={filteredUserAssignedItems}
                onDelete={isAdmin ? handleDeleteAssignedItem : null}
                deleteConfirmMessage={assignedItemDeleteMessage}
                onCountSave={handleCountSave}
                onHistoryClick={handleOpenHistory}
                isSuperAdmin={isAdmin}
                onDistributionClick={isAdmin ? setDistributionItem : null}
                emptyText={emptyText}
              />
            )}
          </div>
        </div>
      </div>

      {/* Ürün Ekle / Düzenle Modal */}
      <Modal
        isOpen={isItemFormOpen}
        onClose={handleCloseItemForm}
        title={editingItem ? "Ürünü Düzenle" : "Yeni Ürün Ekle"}
      >
        <ItemForm
          key={editingItem?._id || "new"}
          initialItem={editingItem}
          onSave={handleSaveItem}
        />
      </Modal>

      {/* Şehre Ata Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title="Ürünü Şehirlere Ata"
      >
        <AssignStockForm
          users={sortByName(cityUsers)}
          items={sortItemsByGroup(safeItems)}
          onAssign={handleAssignStock}
        />
      </Modal>

      {/* Şehir Takibi Modal */}
      <Modal
        isOpen={isCityUpdatesOpen}
        onClose={() => setCityUpdatesOpen(false)}
        title="Şehirlerin Son Stok Güncellemeleri"
      >
        <CityUpdates
          users={users}
          onCityClick={(userId) => {
            setCityUpdatesOpen(false);
            setSelectedUser(userId);
          }}
        />
      </Modal>

      {/* Stok Geçmişi Modal */}
      <Modal
        isOpen={Boolean(historyTarget)}
        onClose={() => setHistoryTarget(null)}
        title={historyTarget ? `${historyTarget.name} - Stok Geçmişi` : "Stok Geçmişi"}
      >
        {historyTarget && (
          <StockHistory userId={historyTarget.userId} itemId={historyTarget.itemId} />
        )}
      </Modal>

      {/* Detay (Distribution) Modal */}
      <Modal
        isOpen={Boolean(distributionItem)}
        onClose={() => setDistributionItem(null)}
        title={
          distributionItem
            ? `${distributionItem.name} - Şehirlere Dağılım`
            : "Stok Dağılımı"
        }
      >
        {distributionData.length === 0 ? (
          <p>Şehir bulunamadı.</p>
        ) : (
          <div className={reportStyles.tableWrap}>
            <p className={reportStyles.muted}>
              {distributionAssigned.length} / {distributionData.length} şehre atanmış ·{" "}
              {distributionAssigned.filter((d) => d.count === 0).length} şehirde stok 0
            </p>
            <table className={reportStyles.table}>
              <thead>
                <tr>
                  <th>Şehir</th>
                  <th>Stok</th>
                </tr>
              </thead>
              <tbody>
                {distributionData.map((d) => (
                  <tr
                    key={d.userId}
                    className={
                      !d.assigned
                        ? reportStyles.unassignedRow
                        : d.count === 0
                        ? reportStyles.zeroRow
                        : ""
                    }
                  >
                    <td>{d.userName}</td>
                    <td>{d.assigned ? d.count : "Atanmamış"}</td>
                  </tr>
                ))}
                <tr className={reportStyles.totalRow}>
                  <td>Toplam</td>
                  <td>{distributionTotal}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Dashboard;
